import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, Pencil, Plus, Search, ShieldCheck, Trash2 } from "lucide-react";

import { useAuthedApi } from "@/api/useAuthedApi";
import { RestError } from "@/api/restClient";
import type { SyncedRole } from "@/api/orgSyncApi";
import type { PermissionGroup, RolesResponse } from "@/api/rolesApi";
import { Button, Chip, Dialog, Spinner, Switch, TextInput } from "@/components/ui";
import { ConfirmDialog } from "@/components/Common/ConfirmDialog";
import { cn } from "@/lib/cn";

// Permission catalog is served by GET /v1/permissions (source of truth is
// `PERMISSION_CATALOG` in libraries/infra/src/infra/auth/fga/engine.py).
// Fetched at mount alongside the role list so the UI never diverges from the
// server's enforcement set.

function humanRole(role: string): string {
  return role.charAt(0).toUpperCase() + role.slice(1).replace(/[-_]/g, " ");
}

/** Workspace-level role administration. Renders once the caller has verified
 *  the user's `roles:manage` permission (cosmetically); the server enforces
 *  every call. Renders a card per role: assignees + a "draft" permission
 *  matrix that saves only when the user confirms — a per-toggle PUT is
 *  hostile for a role redesign. */
export function RolesAdmin() {
  const { roles: rolesApi, orgSync } = useAuthedApi();

  const [data, setData] = useState<RolesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Per-role draft state — permission changes accumulate until the user hits
  // Save. Absence in the map means "no unsaved changes"; presence means the
  // (draft) desired set differs from what the server has.
  const [drafts, setDrafts] = useState<Record<string, Set<string>>>({});
  const [savingRole, setSavingRole] = useState<string | null>(null);
  const [savedRole, setSavedRole] = useState<string | null>(null);
  const [roleError, setRoleError] = useState<Record<string, string>>({});
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [catalog, setCatalog] = useState<PermissionGroup[]>([]);
  const [syncedRoles, setSyncedRoles] = useState<SyncedRole[]>([]);

  const allPermissionKeys = useMemo<string[]>(
    () => catalog.flatMap((g) => g.items.map((i) => i.key)),
    [catalog],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setGlobalError(null);
    try {
      // Fetch the permission catalog alongside the role list so the UI's
      // switch matrix always matches the server's enforcement set.
      const [r, p] = await Promise.all([
        rolesApi.list(),
        rolesApi.permissions(),
      ]);
      setData(r);
      setCatalog(p.groups);
      setForbidden(false);
      try {
        const synced = await orgSync.roles();
        setSyncedRoles(synced.roles ?? []);
      } catch {
        setSyncedRoles([]);
      }
    } catch (e) {
      if (e instanceof RestError && e.status === 403) setForbidden(true);
      else setGlobalError((e as Error).message || "Failed to load roles");
    } finally {
      setLoading(false);
    }
  }, [rolesApi, orgSync]);

  useEffect(() => { void load(); }, [load]);

  const rolesInCompany = useMemo<string[]>(() => {
    if (!data) return [];
    // Prefer the server-supplied enumeration (discovered via
    // `list_company_roles`) — it's the canonical set for this company. Fall
    // back to unioning holders + role_permissions keys if the field is
    // missing (older backend).
    if (data.roles && data.roles.length > 0) return [...data.roles].sort();
    const known = new Set<string>(Object.keys(data.role_permissions));
    for (const h of data.holders) known.add(h.role);
    return Array.from(known).sort();
  }, [data]);

  const holdersByRole = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!data) return map;
    for (const h of data.holders) {
      const list = map.get(h.role) ?? [];
      list.push(h.user_id);
      map.set(h.role, list);
    }
    return map;
  }, [data]);

  const visibleRoles = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rolesInCompany;
    return rolesInCompany.filter((r) => r.toLowerCase().includes(q));
  }, [rolesInCompany, query]);

  const draftFor = (role: string): Set<string> => {
    if (drafts[role]) return drafts[role];
    return new Set(data?.role_permissions[role] ?? []);
  };

  const isDirty = (role: string): boolean => {
    if (!drafts[role]) return false;
    const saved = new Set(data?.role_permissions[role] ?? []);
    const d = drafts[role];
    if (saved.size !== d.size) return true;
    for (const x of saved) if (!d.has(x)) return true;
    return false;
  };

  const togglePermission = (role: string, perm: string, next: boolean) => {
    // Admin permissions are locked (server-side too — PUT returns 409).
    // Silently no-op instead of showing a red banner on a click that shouldn't
    // have been possible.
    if (role === "admin") return;
    setRoleError((e) => ({ ...e, [role]: "" }));
    setDrafts((prev) => {
      const cur = new Set(prev[role] ?? data?.role_permissions[role] ?? []);
      if (next) cur.add(perm);
      else cur.delete(perm);
      return { ...prev, [role]: cur };
    });
  };

  const toggleGroup = (role: string, keys: string[], next: boolean) => {
    if (role === "admin") return;
    setRoleError((e) => ({ ...e, [role]: "" }));
    setDrafts((prev) => {
      const cur = new Set(prev[role] ?? data?.role_permissions[role] ?? []);
      for (const k of keys) {
        if (next) cur.add(k);
        else cur.delete(k);
      }
      return { ...prev, [role]: cur };
    });
  };

  const saveRole = async (role: string) => {
    if (!data) return;
    const desired = Array.from(draftFor(role)).sort();
    setSavingRole(role);
    try {
      const res = await rolesApi.setPermissions(role, desired);
      setData({
        ...data,
        role_permissions: { ...data.role_permissions, [role]: res.permissions },
      });
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[role];
        return next;
      });
      setSavedRole(role);
      setTimeout(() => setSavedRole((r) => (r === role ? null : r)), 1800);
    } catch (e) {
      setRoleError((prev) => ({
        ...prev,
        [role]: (e as Error).message || "Failed to save permissions",
      }));
    } finally {
      setSavingRole(null);
    }
  };

  const discardDraft = (role: string) => {
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[role];
      return next;
    });
    setRoleError((e) => ({ ...e, [role]: "" }));
  };

  const deleteRole = async (role: string) => {
    // ConfirmDialog surfaces errors inline + keeps itself open on failure —
    // rethrow so the dialog does its job.
    setSavingRole(role);
    try {
      await rolesApi.delete(role);
      setDeleteConfirm(null);
      await load();
    } finally {
      setSavingRole(null);
    }
  };

  const renameRole = async (role: string, newName: string) => {
    // Called by EditRoleDialog. Throws on failure so the dialog can display
    // the error inline and stay open.
    await rolesApi.rename(role, newName);
    // Preserve any in-flight permission draft across the rename: the tuples
    // move to the new object id server-side, so the draft's intent applies
    // to the same logical role under its new name.
    setDrafts((prev) => {
      if (!prev[role]) return prev;
      const { [role]: moved, ...rest } = prev;
      return { ...rest, [newName]: moved };
    });
    setEditingRole(null);
    await load();
  };

  const createRole = async (name: string, initialPerms: string[]) => {
    // Roles are lazily materialized in FGA: writing permission tuples on a
    // role name creates it. We PUT the initial permission set — no separate
    // "create" endpoint.
    await rolesApi.setPermissions(name, initialPerms);
    setCreateOpen(false);
    await load();
  };

  if (forbidden) {
    return (
      <div className="rounded-md border border-hairline bg-surface/40 p-6 text-center">
        <ShieldCheck size={18} className="mx-auto text-ink-muted mb-2" />
        <div className="text-[14px] font-semibold text-ink">
          You need the “Manage roles” permission
        </div>
        <p className="text-[12.5px] text-ink-muted m-0 mt-1">
          Ask a company admin to grant it.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Toolbar ──────────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
          />
          <TextInput
            placeholder="Filter roles…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-8"
          />
        </div>
        <div className="flex-1" />
        <Button onClick={() => setCreateOpen(true)}>
          <Plus size={14} /> New role
        </Button>
      </div>

      {syncedRoles.length > 0 && (
        <div className="rounded-md border border-hairline bg-surface/40 p-3 space-y-2">
          <div className="text-[13px] font-semibold text-ink">
            Synced from connected tools
          </div>
          <p className="text-[12px] text-ink-muted m-0">
            MCP role titles. Artie permissions stay empty until you edit a
            matching custom role below.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {syncedRoles.map((r) => (
              <Chip key={`${r.plugin_slug ?? ""}:${r.source_key}`}>
                {humanRole(r.artie_role_name)}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {globalError && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-[12.5px] text-danger">
          {globalError}
        </div>
      )}

      {loading || !data ? (
        <div className="grid place-items-center py-10 text-ink-muted">
          <Spinner />
        </div>
      ) : visibleRoles.length === 0 ? (
        <div className="rounded-md border border-hairline bg-surface/40 p-8 text-center">
          <div className="text-[14px] font-semibold text-ink">No roles yet</div>
          <p className="text-[12.5px] text-ink-muted m-0 mt-1">
            Create your first role to bundle permissions and assign members.
          </p>
          <div className="mt-3">
            <Button onClick={() => setCreateOpen(true)}>
              <Plus size={14} /> New role
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleRoles.map((role) => {
            const holders = holdersByRole.get(role) ?? [];
            const draft = draftFor(role);
            const dirty = isDirty(role);
            const saving = savingRole === role;
            const err = roleError[role];
            const isAdmin = role === "admin";
            // Both built-in roles are rename/delete-locked (server 409's).
            // But only `admin` has its permission set locked — `member`
            // exists precisely so admins can grant a baseline set.
            const isBuiltIn = role === "admin" || role === "member";
            // Auto-expand when there are unsaved changes so the user can see
            // what they're about to save even after they collapsed the card.
            const isOpen = expanded[role] === true || dirty;

            return (
              <div
                key={role}
                className="rounded-md border border-edge bg-surface/40 p-3 space-y-3"
              >
                {/* Header */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <button
                    type="button"
                    onClick={() =>
                      setExpanded((prev) => ({ ...prev, [role]: !isOpen }))
                    }
                    className="flex items-center gap-2 bg-transparent border-0 p-0 cursor-pointer text-left"
                    aria-expanded={isOpen}
                    aria-controls={`role-body-${role}`}
                  >
                    <ChevronDown
                      size={14}
                      className={cn(
                        "text-ink-muted transition-transform",
                        isOpen ? "rotate-0" : "-rotate-90",
                      )}
                    />
                    <span className="text-[15px] font-semibold text-ink">
                      {humanRole(role)}
                    </span>
                    <Chip>
                      {role === "member"
                        ? "Everyone"
                        : `${holders.length} member${holders.length === 1 ? "" : "s"}`}
                    </Chip>
                    {isBuiltIn && (
                      <span className="text-[11px] text-ink-muted">
                        (built-in)
                      </span>
                    )}
                  </button>
                  <div className="flex items-center gap-2">
                    {!isBuiltIn && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingRole(role);
                        }}
                        className="rounded p-1 text-ink-muted hover:bg-hover hover:text-ink bg-transparent border-0 cursor-pointer"
                        aria-label={`Edit ${humanRole(role)}`}
                        title="Edit role"
                      >
                        <Pencil size={12} />
                      </button>
                    )}
                    {saving && <Spinner size={14} />}
                    {savedRole === role && !dirty && (
                      <span className="inline-flex items-center gap-1 text-[12px] font-medium text-accent">
                        <Check size={13} strokeWidth={2.8} /> Saved
                      </span>
                    )}
                    {dirty && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => discardDraft(role)}
                          disabled={saving}
                        >
                          Discard
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => saveRole(role)}
                          disabled={saving}
                        >
                          Save
                        </Button>
                      </>
                    )}
                    {!dirty && !isBuiltIn && holders.length === 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm(role);
                        }}
                        disabled={saving}
                        title="Delete this role"
                      >
                        <Trash2 size={13} /> Delete
                      </Button>
                    )}
                  </div>
                </div>

                {err && (
                  <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-[12.5px] text-danger">
                    {err}
                  </div>
                )}

                {/* Permission matrix */}
                {isOpen && (
                <div className="space-y-2" id={`role-body-${role}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[12px] font-medium text-ink-muted">
                      Permissions
                    </div>
                  </div>
                  <div className="grid gap-2 grid-cols-1 xl:grid-cols-2">
                    {catalog.map((group) => {
                      const groupKeys = group.items.map((i) => i.key);
                      const allOn = isAdmin || groupKeys.every((k) => draft.has(k));
                      return (
                      <div
                        key={group.area}
                        className="rounded-lg border border-hairline bg-canvas p-2.5 space-y-1.5"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">
                            {group.area}
                          </div>
                          {!isAdmin && (
                            <button
                              type="button"
                              onClick={() => toggleGroup(role, groupKeys, !allOn)}
                              disabled={saving}
                              className="text-[10.5px] text-ink-muted hover:text-ink underline-offset-2 hover:underline disabled:opacity-40 disabled:cursor-not-allowed bg-transparent border-0 p-0 cursor-pointer"
                            >
                              {allOn ? "Clear" : "Select all"}
                            </button>
                          )}
                        </div>
                        {group.items.map((p) => {
                          const checked = isAdmin ? true : draft.has(p.key);
                          return (
                            <div
                              key={p.key}
                              className="flex items-center justify-between gap-2"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="text-[12.5px] font-medium text-ink leading-tight">
                                  {p.label}
                                </div>
                                <div className="text-[10.5px] text-ink-muted leading-snug">
                                  {p.description}
                                </div>
                              </div>
                              <Switch
                                label={`${p.label} — ${humanRole(role)}`}
                                checked={checked}
                                onChange={(next) => togglePermission(role, p.key, next)}
                                disabled={saving || isAdmin}
                              />
                            </div>
                          );
                        })}
                      </div>
                      );
                    })}
                  </div>
                </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <CreateRoleDialog
        open={createOpen}
        existing={rolesInCompany}
        catalog={catalog}
        allPermissionKeys={allPermissionKeys}
        onClose={() => setCreateOpen(false)}
        onCreate={createRole}
      />

      <EditRoleDialog
        open={editingRole !== null}
        role={editingRole}
        existing={rolesInCompany}
        onClose={() => setEditingRole(null)}
        onRename={renameRole}
      />

      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Delete this role?"
        body={
          deleteConfirm ? (
            <>
              <strong>{humanRole(deleteConfirm)}</strong> will be removed and its
              permissions cleared. Members with this role won't be affected —
              they keep their other roles.
            </>
          ) : null
        }
        confirmLabel="Delete role"
        cancelLabel="Keep it"
        destructive
        onConfirm={() => (deleteConfirm ? deleteRole(deleteConfirm) : Promise.resolve())}
        onClose={() => setDeleteConfirm(null)}
      />

    </div>
  );
}

// ── Create-role dialog ────────────────────────────────────────────────────

function CreateRoleDialog({
  open,
  existing,
  catalog,
  allPermissionKeys,
  onClose,
  onCreate,
}: {
  open: boolean;
  existing: string[];
  catalog: PermissionGroup[];
  allPermissionKeys: string[];
  onClose: () => void;
  onCreate: (name: string, perms: string[]) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setSelected(new Set());
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const conflict = slug && existing.includes(slug);
  const canSubmit = slug.length > 0 && !conflict && !busy;

  const toggle = (perm: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(perm)) next.delete(perm);
      else next.add(perm);
      return next;
    });
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await onCreate(slug, Array.from(selected));
    } catch (e) {
      setError((e as Error).message || "Failed to create role");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="New role" size="lg">
      <div className="px-6 py-5 space-y-5">
        <div className="space-y-1.5">
          <label htmlFor="role-name" className="text-[13px] font-medium text-ink">
            Role name
          </label>
          <TextInput
            id="role-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sales manager"
            autoFocus
          />
          {slug && (
            <p className="text-[11.5px] text-ink-muted m-0">
              Identifier: <code className="text-ink">{slug}</code>
              {conflict && (
                <span className="text-danger"> — already exists</span>
              )}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <div className="text-[13px] font-medium text-ink">Initial permissions</div>
          <div className="grid gap-2 sm:grid-cols-2">
            {catalog.map((group) => (
              <div
                key={group.area}
                className="rounded-lg border border-hairline bg-canvas p-3 space-y-2"
              >
                <div className="text-[11.5px] font-semibold uppercase tracking-wide text-ink-muted">
                  {group.area}
                </div>
                {group.items.map((p) => {
                  const on = selected.has(p.key);
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => toggle(p.key)}
                      className="flex w-full items-start gap-2 rounded-md p-1 text-left hover:bg-hover cursor-pointer bg-transparent border-0"
                    >
                      <span
                        aria-hidden
                        className={cn(
                          "mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border",
                          on
                            ? "bg-accent border-accent text-white"
                            : "border-edge text-transparent",
                        )}
                      >
                        {on && <Check size={11} strokeWidth={3} />}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[13px] font-medium text-ink">
                          {p.label}
                        </span>
                        <span className="block text-[11.5px] text-ink-muted">
                          {p.description}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelected(new Set(allPermissionKeys))}
              disabled={busy}
            >
              Select all
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelected(new Set())}
              disabled={busy}
            >
              Clear
            </Button>
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-[12.5px] text-danger">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-4 border-t border-hairline -mx-6 px-6">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {busy && <Spinner size={16} className="text-white" />}
            Create role
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Edit-role dialog ──────────────────────────────────────────────────────

/** Edit dialog for an existing role. Currently exposes rename only; future
 *  role-level fields (description, icon, tenant-scoped policy toggles) belong
 *  here so the surface is one place rather than N inline widgets. */
function EditRoleDialog({
  open,
  role,
  existing,
  onClose,
  onRename,
}: {
  open: boolean;
  role: string | null;
  existing: string[];
  onClose: () => void;
  onRename: (role: string, newName: string) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && role) {
      setName(role);
      setError(null);
      setBusy(false);
    }
  }, [open, role]);

  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const unchanged = role !== null && slug === role;
  const conflict = slug.length > 0 && !unchanged && existing.includes(slug);
  const canSubmit = slug.length > 0 && !unchanged && !conflict && !busy;

  const submit = async () => {
    if (!canSubmit || !role) return;
    setBusy(true);
    setError(null);
    try {
      await onRename(role, slug);
    } catch (e) {
      setError((e as Error).message || "Failed to update role");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Edit role" size="md">
      <div className="px-6 py-5 space-y-5">
        <div className="space-y-1.5">
          <label htmlFor="edit-role-name" className="text-[13px] font-medium text-ink">
            Role name
          </label>
          <TextInput
            id="edit-role-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sales manager"
            autoFocus
            disabled={busy}
          />
          {slug && (
            <p className="text-[11.5px] text-ink-muted m-0">
              Identifier: <code className="text-ink">{slug}</code>
              {conflict && (
                <span className="text-danger"> — already exists</span>
              )}
            </p>
          )}
        </div>

        {error && (
          <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-[12.5px] text-danger">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-4 border-t border-hairline -mx-6 px-6">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {busy && <Spinner size={16} className="text-white" />}
            Save changes
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
